type ExecutionLike = Record<string, any>;

export const normalizeProviderRunKey = (value?: string | null) => {
  const key = String(value || "").toUpperCase();
  if (!key) return "";
  if (key.includes("PERP")) return "PERP";
  if (
    key.includes("AI_MODE") ||
    key.includes("AI-MODE") ||
    key.includes("AIMODE")
  ) {
    return "AI_MODE";
  }
  if (key.includes("GEMINI")) return "GEMINI";
  if (
    key.includes("GOOGLE") ||
    key.includes("AIO") ||
    key.includes("OVERVIEW")
  ) {
    return "GOOGLE_OVERVIEW";
  }
  if (key.includes("GROK")) return "GROK";
  if (key.includes("COPILOT")) return "COPILOT";
  if (key.includes("GPT")) return "GPT";
  return key;
};

export const normalizeProviderStatusKey = (value?: string | null) => {
  const status = String(value || "").trim().toLowerCase();
  if (!status) return "never_run";
  if (status === "never_run") return "never_run";
  if (status === "not_triggered") return "not_triggered";
  if (status === "snapshot_pending") return "snapshot_pending";
  if (status === "processing") return "processing";
  if (status === "running") return "running";
  if (status === "queued") return "queued";
  if (status === "failed") return "failed";
  if (status === "quota_exceeded") return "quota_exceeded";
  if (status === "completed") return "completed";
  return status;
};

export const toExecutionDateKey = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const direct = raw.match(/^\d{4}-\d{2}-\d{2}/);
  if (direct?.[0]) return direct[0];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

export const buildProviderRank = (expectedProviders?: string[]) => {
  const normalizedExpectedProviders = Array.from(
    new Set(
      (expectedProviders || [])
        .map((provider) => normalizeProviderRunKey(provider))
        .filter(Boolean),
    ),
  );
  const providerRank = new Map<string, number>();
  normalizedExpectedProviders.forEach((provider, index) => {
    providerRank.set(provider, index);
  });
  return {
    normalizedExpectedProviders,
    providerRank,
  };
};

export const compareExecutionsByDateAndProvider = (
  a: ExecutionLike,
  b: ExecutionLike,
  providerRank: Map<string, number>,
) => {
  const dateKeyA = toExecutionDateKey(a?.runDate || a?.createdAt || a?.created_at);
  const dateKeyB = toExecutionDateKey(b?.runDate || b?.createdAt || b?.created_at);
  const dateA = Date.parse(dateKeyA || "");
  const dateB = Date.parse(dateKeyB || "");
  const safeDateA = Number.isFinite(dateA) ? dateA : 0;
  const safeDateB = Number.isFinite(dateB) ? dateB : 0;
  if (safeDateA !== safeDateB) {
    return safeDateB - safeDateA;
  }

  const providerA = normalizeProviderRunKey(a?.provider);
  const providerB = normalizeProviderRunKey(b?.provider);
  const rankA = providerRank.get(providerA);
  const rankB = providerRank.get(providerB);
  const hasRankA = rankA !== undefined;
  const hasRankB = rankB !== undefined;

  if (hasRankA && hasRankB && rankA !== rankB) {
    return rankA - rankB;
  }

  if (hasRankA !== hasRankB) {
    return hasRankA ? -1 : 1;
  }

  if (!hasRankA && !hasRankB) {
    return providerA.localeCompare(providerB);
  }

  return 0;
};

export const buildPromptsDetailsExecutionsTableData = ({
  runs,
  promptProviderStatusHistory,
  expectedProviders,
  search,
  includePlaceholders = true,
}: {
  runs: ExecutionLike[];
  promptProviderStatusHistory?: ExecutionLike[];
  expectedProviders?: string[];
  search?: string;
  includePlaceholders?: boolean;
}) => {
  const normalizedSearch = String(search || "").trim().toLowerCase();
  const { normalizedExpectedProviders, providerRank } =
    buildProviderRank(expectedProviders);

  const sortExecutionsByDateDesc = (a: ExecutionLike, b: ExecutionLike) =>
    compareExecutionsByDateAndProvider(a, b, providerRank);

  const filtered =
    runs.filter((run: ExecutionLike) => {
      if (!normalizedSearch) return true;
      const responseText = String(run?.responseText || run?.responsePreview || "");
      const statusText = String(run?.status || "");
      const providerText = String(run?.provider || "");
      const searchBlob = `${responseText} ${statusText} ${providerText}`.toLowerCase();
      return searchBlob.includes(normalizedSearch);
    }) || [];

  const sorted = filtered.slice().sort(sortExecutionsByDateDesc);

  const providersToCheck = normalizedExpectedProviders;
  if (!includePlaceholders || !providersToCheck.length) {
    return sorted;
  }

  const existingRunByDateProvider = new Set<string>();
  runs.forEach((run: ExecutionLike) => {
    const dateKey = toExecutionDateKey(run?.runDate || run?.createdAt);
    if (!dateKey) return;
    const providerKey = normalizeProviderRunKey(run?.provider);
    if (!providerKey) return;
    existingRunByDateProvider.add(`${dateKey}::${providerKey}`);
  });

  const placeholderRows: ExecutionLike[] = [];
  const placeholderKeys = new Set<string>();
  const matchesSearch = (value: string) =>
    !normalizedSearch || value.toLowerCase().includes(normalizedSearch);

  const addPlaceholder = (
    provider: string,
    dateKey: string,
    status: string,
  ) => {
    if (!provider || !dateKey || !status) return;
    const key = `${dateKey}::${provider}`;
    if (placeholderKeys.has(key) || existingRunByDateProvider.has(key)) return;
    if (
      status === "running" ||
      status === "queued" ||
      status === "snapshot_pending" ||
      status === "processing"
    ) {
      if (!matchesSearch("In Progress")) return;
      placeholderRows.push({
        runDate: dateKey,
        responseText: "In Progress",
        citedDomains: [],
        isLoadingRow: true,
        provider,
        status,
      });
      placeholderKeys.add(key);
      return;
    }
    if (status === "not_triggered") {
      if (!matchesSearch("Not Triggered")) return;
      placeholderRows.push({
        runDate: dateKey,
        responseText: "Not Triggered",
        citedDomains: [],
        isLoadingRow: false,
        provider,
        status: "not_triggered",
      });
      placeholderKeys.add(key);
    }
  };

  const historyRows = Array.isArray(promptProviderStatusHistory)
    ? promptProviderStatusHistory
    : [];
  historyRows.forEach((entry: ExecutionLike) => {
    const provider = normalizeProviderRunKey(entry?.provider);
    if (!provider || !providersToCheck.includes(provider)) return;
    const status = normalizeProviderStatusKey(entry?.status);
    const dateKey = toExecutionDateKey(entry?.runDate || entry?.lastRunAt);
    addPlaceholder(provider, dateKey, status);
  });

  return [...sorted, ...placeholderRows].sort(sortExecutionsByDateDesc);
};

